/**
 * SchemaBrowserModal — full-screen overlay that wraps SchemaNodeListPanel
 * in selection mode so analysts can pick one or more tables from the full
 * schema and pump them straight into the SQL Investigation editor as
 * starter SELECT cards.
 *
 * Triggered from a "🔍 Browse all" button in the SQL Investigation
 * Suggested Tables header. Closes on the × button, the backdrop click,
 * or Escape. No backend changes — reuses `useSchemaGraphData` from
 * graphLoader, which caches the same graph + documents the Graph Viz
 * page already fetches.
 */

import { useCallback, useEffect, useState } from 'react';
import { SchemaNodeListPanel } from './SchemaNodeListPanel';
import { useSchemaGraphData } from './graphLoader';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called when the analyst clicks "Add N cards". Receives the FQNs of
   *  the selected tables; the parent should turn each into a starter
   *  SELECT card and close the modal. */
  onAddTables: (fqns: string[]) => void;
}

export function SchemaBrowserModal({ open, onClose, onAddTables }: Props) {
  const { graphData, documents, loading, error } = useSchemaGraphData();
  const [selectedFqns, setSelectedFqns] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);

  // Clear selection whenever the modal opens fresh.
  useEffect(() => {
    if (open) {
      setSelectedFqns(new Set());
      setCopied(false);
    }
  }, [open]);

  // Escape to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const toggleSelection = useCallback((fqn: string) => {
    setSelectedFqns((prev) => {
      const next = new Set(prev);
      if (next.has(fqn)) next.delete(fqn);
      else next.add(fqn);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedFqns.size === 0) return;
    onAddTables(Array.from(selectedFqns));
  }, [selectedFqns, onAddTables]);

  const handleCopy = useCallback(() => {
    if (selectedFqns.size === 0) return;
    const text = Array.from(selectedFqns).join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [selectedFqns]);

  if (!open) return null;

  const count = selectedFqns.size;
  const canConfirm = count > 0;

  const footer = (
    <div style={{
      padding: '10px 14px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 10, background: '#f9fafb',
    }}>
      <span style={{ fontSize: 11, color: '#374151' }}>
        {count === 0
          ? 'Tick rows to select tables — multiple OK.'
          : `${count} table${count === 1 ? '' : 's'} selected`}
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!canConfirm}
          title="Copy selected FQNs to clipboard, one per line"
          style={{
            padding: '5px 10px', fontSize: 11, fontWeight: 500,
            cursor: canConfirm ? 'pointer' : 'default',
            border: '1px solid #d1d5db', borderRadius: 6,
            background: copied ? '#d1fae5' : 'white',
            color: canConfirm ? (copied ? '#065f46' : '#374151') : '#9ca3af',
            opacity: canConfirm ? 1 : 0.5,
          }}
        >
          {copied ? '✓ Copied' : `📋 Copy ${count > 0 ? count : ''} FQN${count === 1 ? '' : 's'}`}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm}
          style={{
            padding: '5px 10px', fontSize: 11, fontWeight: 600,
            cursor: canConfirm ? 'pointer' : 'default',
            border: '1px solid #4f46e5', borderRadius: 6,
            background: canConfirm ? '#4f46e5' : '#c7d2fe',
            color: 'white',
          }}
        >
          + Add {count > 0 ? count : ''} card{count === 1 ? '' : 's'}
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '5px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer',
            border: '1px solid #d1d5db', borderRadius: 6, background: 'white', color: '#374151',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop — click to close. */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
          zIndex: 40,
        }}
        aria-hidden
      />

      {/* Modal shell — centred, max 90vw × 80vh. Stops click propagation
          so clicking inside the modal doesn't close it. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Schema Browser"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(90vw, 1200px)', height: 'min(80vh, 760px)',
          background: 'white', borderRadius: 10, overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          zIndex: 50,
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: '10px 14px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
              Schema Browser
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              Pick tables to add as starter cards in SQL Investigation.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Schema Browser"
            style={{
              padding: '3px 9px', fontSize: 17, cursor: 'pointer', lineHeight: 1,
              border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', color: '#6b7280',
            }}
          >×</button>
        </div>

        {/* Body — either the panel, a loading state, or an error. */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {loading && (
            <div style={{ padding: 30, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>
              Loading schema…
            </div>
          )}
          {!loading && error && (
            <div style={{ padding: 16 }}>
              <div style={{
                padding: 10, fontSize: 12, color: '#7f1d1d',
                background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6,
              }}>
                Failed to load schema: {error}
              </div>
            </div>
          )}
          {!loading && !error && (
            <SchemaNodeListPanel
              open
              onClose={onClose}
              graphData={graphData}
              documents={documents}
              embedded
              selectionMode="rows"
              selectedFqns={selectedFqns}
              onToggleSelection={toggleSelection}
              footerSlot={footer}
            />
          )}
        </div>
      </div>
    </>
  );
}
